import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import useScrollAnimation from "../hooks/useScrollAnimation";

// Stagger animation variants for feature cards
const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};
const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

const FEATURES = [
  {
    icon: "✦",
    title: "Chat with AI",
    description:
      "Ask questions about machine learning, deep learning, NLP — and get grounded answers with direct citations from arXiv research papers.",
    cta: "Start chatting",
    link: "/chat",
    accent: "var(--accent)",
  },
  {
    icon: "⊕",
    title: "Glossary",
    description:
      "100+ entries covering ML fundamentals, Transformers, RAG, Agents, frontier models (GPT-4o, Claude, Gemini), and the labs shaping the AI landscape.",
    cta: "Browse terms",
    link: "/vocabulary",
    accent: "var(--accent2)",
  },
  {
    icon: "◈",
    title: "Agentic RAG",
    description:
      "Every answer runs through an autonomous pipeline — guardrail → retrieve → grade → rewrite — so you only get relevant, high-quality results.",
    cta: "Learn more",
    link: "/chat",
    accent: "var(--accent3)",
  },
];

export default function Home() {
  useScrollAnimation(".scroll-reveal");

  return (
    <>
      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="hero">
        <div className="container hero__inner">
          <motion.div
            className="hero__text"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="badge hero__eyebrow">
              AI · Research · Knowledge
            </span>
            <h1 className="hero__title">
              Arcane <span className="text-gradient">Illumina</span>
            </h1>
            <p className="hero__subtitle">
              The living knowledge platform for AI research papers. Ask,
              explore, and learn about machine learning through intelligent
              conversation —{" "}
              <span className="text-accent">grounded in real arXiv papers</span>
              .
            </p>
            <div className="hero__ctas">
              <Link to="/chat" className="btn btn-primary">
                Ask AI →
              </Link>
              <Link to="/vocabulary" className="btn btn-secondary">
                Glossary
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────────── */}
      <section className="section features">
        <div className="container">
          <div className="section-header scroll-reveal">
            <h2>Everything you need to learn about AI</h2>
            <span className="accent-line" />
            <p>Built with passion. Powered by cutting-edge research.</p>
          </div>

          <motion.div
            className="grid-3 scroll-reveal"
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            {FEATURES.map((f) => (
              <motion.div
                key={f.title}
                className="card feature-card"
                variants={cardVariants}
              >
                <div className="feature-card__icon" style={{ color: f.accent }}>
                  {f.icon}
                </div>
                <h3 className="feature-card__title">{f.title}</h3>
                <p className="feature-card__desc text-muted">{f.description}</p>
                <Link to={f.link} className="feature-card__cta">
                  {f.cta} →
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Built by ────────────────────────────────────────────────────── */}
      <section className="section built-by-section">
        <div className="container">
          <div className="section-header scroll-reveal">
            <h2>Built by</h2>
            <span className="accent-line" />
          </div>
          <div className="built-by-card scroll-reveal">
            <div className="built-by-avatar">
              <img
                src="/avatar.jpg"
                alt="Adil Ibraheem Koyava"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div className="built-by-avatar__fallback">AIK</div>
            </div>
            <div className="built-by-info">
              <h3 className="built-by-name">Adil Ibraheem Koyava</h3>
              <p className="built-by-role text-muted">
                Data Scientist · AI Engineer · Product Manager
              </p>
              <p className="built-by-bio text-muted">
                M.Sc. Business Intelligence & Data Analytics, Emden/Leer
                University of Applied Sciences. 3+ years as Associate Product
                Manager — turning data into decisions, and decisions into
                shipped products.
              </p>
              <div className="built-by-links">
                <a
                  href="https://www.linkedin.com/in/adil-ibraheem-koyava/"
                  target="_blank"
                  rel="noreferrer"
                  className="built-by-link"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  LinkedIn
                </a>
                <a
                  href="https://github.com/koyava"
                  target="_blank"
                  rel="noreferrer"
                  className="built-by-link"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12" />
                  </svg>
                  GitHub
                </a>
                <span className="built-by-location text-muted">
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 10c0 6-8 13-8 13s-8-7-8-13a8 8 0 0 1 16 0Z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  Hamburg, Germany
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        /* ── Hero ── */
        .hero {
          min-height: calc(100vh - var(--nav-height));
          display: flex;
          align-items: center;
          padding: var(--space-3xl) 0 var(--space-2xl);
        }
        .hero__inner { max-width: 660px; }
        .hero__eyebrow { margin-bottom: var(--space-lg); display: block; }
        .hero__title {
          margin-bottom: var(--space-lg);
          color: var(--text);
        }
        .hero__subtitle {
          font-size: 1.1rem;
          color: var(--text-muted);
          line-height: 1.7;
          margin-bottom: var(--space-xl);
          max-width: 520px;
        }
        .hero__ctas {
          display: flex;
          gap: var(--space-md);
          flex-wrap: wrap;
        }

        /* ── Feature cards ── */
        .feature-card { text-align: left; }
        .feature-card__icon {
          font-size: 1.8rem;
          margin-bottom: var(--space-md);
          display: block;
        }
        .feature-card__title {
          font-family: var(--font-heading);
          font-size: 1.05rem;
          margin-bottom: var(--space-sm);
        }
        .feature-card__desc {
          font-size: 0.88rem;
          line-height: 1.6;
          margin-bottom: var(--space-md);
        }
        .feature-card__cta {
          font-size: 0.85rem;
          color: var(--accent);
          font-weight: 500;
        }
        .feature-card__cta:hover { opacity: 0.75; }

        /* ── Scroll reveal ── */
        .scroll-reveal {
          opacity: 0;
          transform: translateY(18px);
          transition: opacity 0.55s ease, transform 0.55s ease;
        }
        .scroll-reveal.visible {
          opacity: 1;
          transform: none;
        }

        /* ── Built by ── */
        .built-by-section { padding-bottom: var(--space-3xl); }
        .built-by-card {
          display: flex;
          gap: var(--space-xl);
          align-items: flex-start;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: var(--space-xl);
          max-width: 680px;
          margin: 0 auto;
        }
        .built-by-avatar {
          flex-shrink: 0;
          width: 88px;
          height: 88px;
          border-radius: 50%;
          overflow: hidden;
          border: 2px solid var(--border);
          background: var(--accent-soft);
          position: relative;
        }
        .built-by-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .built-by-avatar__fallback {
          display: none;
          width: 100%;
          height: 100%;
          align-items: center;
          justify-content: center;
          font-family: var(--font-heading);
          font-weight: 700;
          font-size: 1.3rem;
          color: var(--accent);
        }
        .built-by-name {
          font-family: var(--font-heading);
          font-size: 1.15rem;
          margin-bottom: 4px;
          color: var(--text);
        }
        .built-by-role {
          font-size: 0.82rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: var(--space-sm);
        }
        .built-by-bio {
          font-size: 0.88rem;
          line-height: 1.65;
          margin-bottom: var(--space-md);
        }
        .built-by-links {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          flex-wrap: wrap;
        }
        .built-by-link {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.85rem;
          font-weight: 500;
          color: var(--accent);
          transition: opacity 0.2s;
        }
        .built-by-link:hover { opacity: 0.7; }
        .built-by-location {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 0.82rem;
        }
        @media (max-width: 500px) {
          .built-by-card { flex-direction: column; align-items: center; text-align: center; }
          .built-by-links { justify-content: center; }
        }
      `}</style>
    </>
  );
}
